/**
 * Fleet Simulation - Scallops: play/cancel scallop fleet animation.
 * Owns play/cancel logic; receives scallopFleetActive and setScallopFleetActive from injections.
 * Auto-plays when provided in layout as initial layer.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import type { RegistryComponentProps } from '../../../pages/registry/componentRegistry';
import BetaChip from '../../../components/common/BetaChip/BetaChip';

interface CesiumEntity {
  id?: string;
  properties?: { getValue?: () => { dataType?: string }; dataType?: string };
  label?: { text?: string | { getValue?: () => string } };
}

interface CesiumViewer {
  isDestroyed?: () => boolean;
  entities?: {
    values?: CesiumEntity[];
    remove?: (entity: CesiumEntity) => void;
  };
  clock?: { shouldAnimate?: boolean };
}

interface ScallopInjections {
  scallopFleetActive?: boolean;
  setScallopFleetActive?: (active: boolean) => void;
  viewer?: CesiumViewer;
  fleetAnimationTrigger?: number;
  onFleetAnimationCancel?: () => void;
  fleetDataLoading?: boolean;
  initialLayerSlugs?: string[];
}

export default function PlayScallopSimulationDataLayer(
  props: RegistryComponentProps
): React.ReactElement {
  const { injections = {}, componentSlug } = props;
  const {
    scallopFleetActive = false,
    setScallopFleetActive = () => {},
    viewer,
    fleetAnimationTrigger = 0,
    onFleetAnimationCancel = () => {},
    fleetDataLoading = false,
    initialLayerSlugs = [],
  } = injections as ScallopInjections;

  const autoPlayTriggeredRef = useRef(false);
  const isInInitialLayers = initialLayerSlugs.includes(componentSlug);

  // When present in bottom panel active-data-layers, auto-play after mount.
  useEffect(() => {
    if (!isInInitialLayers) return;
    if (autoPlayTriggeredRef.current) return;
    if (fleetDataLoading) return;

    autoPlayTriggeredRef.current = true;
    if (fleetAnimationTrigger > 0 && fleetAnimationTrigger % 2 === 1) {
      onFleetAnimationCancel();
    }
    setScallopFleetActive(true);
  }, [isInInitialLayers, fleetDataLoading, fleetAnimationTrigger, onFleetAnimationCancel, setScallopFleetActive]);

  const handleScallopFleetPlay = useCallback(() => {
    if (fleetAnimationTrigger > 0 && fleetAnimationTrigger % 2 === 1) {
      onFleetAnimationCancel();
    }
    setScallopFleetActive(true);
  }, [fleetAnimationTrigger, onFleetAnimationCancel, setScallopFleetActive]);

  const handleScallopFleetCancel = useCallback(() => {
    setScallopFleetActive(false);
    if (viewer && !viewer.isDestroyed?.()) {
      const entitiesToRemove: CesiumEntity[] = [];
      viewer.entities?.values?.forEach?.((entity: CesiumEntity) => {
        const entityProps = entity.properties;
        if (entityProps) {
          const dataType = entityProps.getValue
            ? entityProps.getValue().dataType
            : entityProps.dataType;
          const entityId = entity.id || '';
          const label = entity.label;
          const labelText =
            label && label.text
              ? typeof label.text === 'object' && 'getValue' in label.text
                ? (label.text as { getValue?: () => string }).getValue?.()
                : String(label.text)
              : '';
          if (
            dataType === 'vessel' &&
            labelText &&
            labelText.includes('Scallop')
          ) {
            entitiesToRemove.push(entity);
          } else if (
            entityId.includes('scallop') ||
            (labelText && labelText.includes('Scallop'))
          ) {
            entitiesToRemove.push(entity);
          }
        }
      });
      entitiesToRemove.forEach((entity) => {
        try {
          viewer.entities?.remove?.(entity);
        } catch (err: unknown) {
          console.warn('Error removing scallop fleet entity:', err);
        }
      });
      const isRegularFleetActive =
        fleetAnimationTrigger > 0 && fleetAnimationTrigger % 2 === 1;
      if (!isRegularFleetActive && viewer.clock) {
        viewer.clock.shouldAnimate = false;
      }
    }
  }, [viewer, fleetAnimationTrigger, setScallopFleetActive]);

  return (
    <div className="control-section">
      <div className="section-title">
        <span className="section-icon">
          Fleet Simulation - Scallops <BetaChip />
        </span>
      </div>
      <div
        className="form-group"
        style={{ marginBottom: '15px', marginTop: '15px' }}
      >
        <label className="form-label">Scallop Fleet</label>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '12px',
            color: '#4b5563',
            padding: '8px',
            backgroundColor: '#f9fafb',
            borderRadius: '4px',
            border: '1px solid #e5e7eb',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                display: 'inline-block',
                width: '14px',
                height: '12px',
                backgroundColor: scallopFleetActive ? '#f59e0b' : '#6b7280',
                borderRadius: '50%',
                marginRight: '8px',
                border: '2px solid white',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.2)',
              }}
            />
            <span style={{ fontWeight: '500' }}>
              {scallopFleetActive ? 'Scallop Fleet Active' : 'Scallop Fleet Stopped'} - 20 Vessels
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={handleScallopFleetPlay}
              disabled={fleetDataLoading}
              style={{
                padding: '8px 12px',
                fontSize: '12px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: fleetDataLoading ? 'not-allowed' : 'pointer',
                opacity: fleetDataLoading ? 0.5 : 1,
                fontWeight: '500',
                maxWidth: '35px',
              }}
            >
              ▶
            </button>
            <button
              onClick={handleScallopFleetCancel}
              disabled={fleetDataLoading}
              style={{
                padding: '8px 12px',
                fontSize: '12px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: fleetDataLoading ? 'not-allowed' : 'pointer',
                opacity: fleetDataLoading ? 0.5 : 1,
                fontWeight: '1000',
                maxWidth: '35px',
              }}
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
